// 학습 통계 저장 모듈.
// 모든 라운드의 문제별 결과를 국가 단위(ISO2)로 localStorage에 누적한다.
// localStorage 실패 시(quota/private mode) silent in-memory fallback.

import type { PoolKey, QuizMode } from './types.js';

const STORAGE_KEY = 'name-nation.stats.v1';
const SCHEMA_VERSION = 1 as const;
const BEST_KEY = 'name-nation.best.v1';
const BEST_SCHEMA_VERSION = 1 as const;

export const MIN_ATTEMPTS_FOR_REVIEW = 2;

export interface CountryStat {
  attempts: number;
  correct: number;
  lastSeen: number; // unix ms
}

export interface OverallStat {
  rounds: number;
  totalQuestions: number;
  totalCorrect: number;
}

export interface StatsV1 {
  version: typeof SCHEMA_VERSION;
  countries: Record<string, CountryStat>;
  overall: OverallStat;
}

let memCache: StatsV1 | null = null;

function emptyStats(): StatsV1 {
  return {
    version: SCHEMA_VERSION,
    countries: {},
    overall: { rounds: 0, totalQuestions: 0, totalCorrect: 0 },
  };
}

function safeReadStats(): StatsV1 {
  if (memCache !== null) return memCache;
  try {
    if (typeof localStorage === 'undefined') return emptyStats();
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return emptyStats();
    const parsed = JSON.parse(raw) as Partial<StatsV1>;
    if (parsed.version !== SCHEMA_VERSION) return emptyStats();
    return {
      version: SCHEMA_VERSION,
      countries: parsed.countries ?? {},
      overall: parsed.overall ?? { rounds: 0, totalQuestions: 0, totalCorrect: 0 },
    };
  } catch {
    return emptyStats();
  }
}

function safeWriteStats(stats: StatsV1): void {
  try {
    if (typeof localStorage === 'undefined') {
      memCache = stats;
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
    memCache = null; // localStorage가 truth source가 되었으므로 비움
  } catch {
    memCache = stats;
  }
}

export function recordResult(iso2: string, correct: boolean, now: number = Date.now()): void {
  const stats = safeReadStats();
  const prev = stats.countries[iso2] ?? { attempts: 0, correct: 0, lastSeen: 0 };
  stats.countries[iso2] = {
    attempts: prev.attempts + 1,
    correct: prev.correct + (correct ? 1 : 0),
    lastSeen: now,
  };
  safeWriteStats(stats);
}

export function recordRoundComplete(totalQuestions: number, totalCorrect: number): void {
  const stats = safeReadStats();
  stats.overall = {
    rounds: stats.overall.rounds + 1,
    totalQuestions: stats.overall.totalQuestions + totalQuestions,
    totalCorrect: stats.overall.totalCorrect + totalCorrect,
  };
  safeWriteStats(stats);
}

export function getCountryStat(iso2: string): CountryStat | null {
  const stats = safeReadStats();
  return stats.countries[iso2] ?? null;
}

export function getOverall(): OverallStat {
  return safeReadStats().overall;
}

export function getWeakest(n: number): Array<{ iso2: string; stat: CountryStat }> {
  const stats = safeReadStats();
  const list: Array<{ iso2: string; stat: CountryStat }> = [];
  for (const [iso2, stat] of Object.entries(stats.countries)) {
    if (stat.attempts >= MIN_ATTEMPTS_FOR_REVIEW) {
      list.push({ iso2, stat });
    }
  }
  list.sort((a, b) => {
    const wrongA = 1 - a.stat.correct / a.stat.attempts;
    const wrongB = 1 - b.stat.correct / b.stat.attempts;
    if (wrongA !== wrongB) return wrongB - wrongA;
    return b.stat.attempts - a.stat.attempts;
  });
  return list.slice(0, n);
}

/**
 * 복습 풀이 의미 있는지 — 자격 있는 국가 중 적어도 1개라도 weight > 0인지.
 * GlobeQuiz·StartMenu 양쪽에서 동일한 판단을 쓰도록 단일 위치에 둠.
 */
export function hasReviewableData(): boolean {
  const eligible = getEligibleForReview();
  return eligible.some((iso) => getReviewWeight(iso) > 0);
}

export function getEligibleForReview(minAttempts: number = MIN_ATTEMPTS_FOR_REVIEW): string[] {
  const stats = safeReadStats();
  const out: string[] = [];
  for (const [iso2, stat] of Object.entries(stats.countries)) {
    if (stat.attempts >= minAttempts) out.push(iso2);
  }
  return out;
}

export function getReviewWeight(iso2: string, now: number = Date.now()): number {
  const stat = getCountryStat(iso2);
  if (stat === null || stat.attempts < MIN_ATTEMPTS_FOR_REVIEW) return 0;
  const wrongRate = 1 - stat.correct / stat.attempts;
  if (wrongRate <= 0) return 0;
  const daysSinceSeen = (now - stat.lastSeen) / 86400000;
  return wrongRate * Math.log(1 + Math.max(0, daysSinceSeen));
}

export function clearAll(): void {
  memCache = null;
  bestMemCache = null;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BEST_KEY);
    }
  } catch {
    // ignore
  }
}

// ============================================================
// 베스트 점수 — 풀 × 모드 조합별로 가장 높은 점수만 저장.
// 학습 통계(위)와는 독립. 역할: 학습 진도 vs 동기부여.
// ============================================================

export interface BestRecord {
  version: typeof BEST_SCHEMA_VERSION;
  // pool key -> mode -> highest score
  pools: Record<string, Partial<Record<QuizMode, number>>>;
}

let bestMemCache: BestRecord | null = null;

function emptyBest(): BestRecord {
  return { version: BEST_SCHEMA_VERSION, pools: {} };
}

function safeReadBest(): BestRecord {
  if (bestMemCache !== null) return bestMemCache;
  try {
    if (typeof localStorage === 'undefined') return emptyBest();
    const raw = localStorage.getItem(BEST_KEY);
    if (raw === null) return emptyBest();
    const parsed = JSON.parse(raw) as Partial<BestRecord>;
    if (parsed.version !== BEST_SCHEMA_VERSION) return emptyBest();
    return {
      version: BEST_SCHEMA_VERSION,
      pools: parsed.pools ?? {},
    };
  } catch {
    return emptyBest();
  }
}

function safeWriteBest(b: BestRecord): void {
  try {
    if (typeof localStorage === 'undefined') {
      bestMemCache = b;
      return;
    }
    localStorage.setItem(BEST_KEY, JSON.stringify(b));
    bestMemCache = null;
  } catch {
    bestMemCache = b;
  }
}

export function getBest(pool: PoolKey, quizMode: QuizMode): number | null {
  const b = safeReadBest();
  const p = b.pools[pool];
  if (!p) return null;
  return p[quizMode] ?? null;
}

/**
 * 점수가 기존 베스트보다 높으면 갱신. 결과로 직전 베스트(없으면 null)와
 * 갱신 여부를 함께 돌려준다 — UI에서 "🏆 베스트!" 배지를 띄울지 결정용.
 */
export function recordBest(
  pool: PoolKey,
  quizMode: QuizMode,
  score: number,
): { isNew: boolean; previous: number | null } {
  const b = safeReadBest();
  const p: Partial<Record<QuizMode, number>> = b.pools[pool] ?? {};
  const prev = p[quizMode] ?? null;
  if (prev === null || score > prev) {
    p[quizMode] = score;
    b.pools[pool] = p;
    safeWriteBest(b);
    return { isNew: true, previous: prev };
  }
  return { isNew: false, previous: prev };
}
